import Big from 'big.js';
import {combineLatest} from 'rxjs';
import {throttleTime, map, startWith} from 'rxjs/operators';
import {contractUtil} from '@/helper/contract';
import {positionsCache$} from '@/positions/positions-cache';
import {contractMarketRealtimeCache$} from '@/market-contract/market24h-contract-cache';
import {contractDefaultTakerFeeRateEr$, contractsCache$} from '@/config';
import type {ws, ContractMarket, ContractPair} from '@/types';

type ContractPositionRaw = ws.ContractPositionRaw;

// 计算totalPosCost
export const totalPosCost$ = combineLatest([
  positionsCache$,
  contractMarketRealtimeCache$,
  contractsCache$,
  contractDefaultTakerFeeRateEr$,
]).pipe(throttleTime(500), map(calcPostCost), startWith(new Map<string, Big>()));

function calcPostCost([positionsCache, marketsCache, contractsCache, defaultTakerFeeEr]: [
  Map<string, ContractPositionRaw>,
  Map<string, ContractMarket>,
  Map<string, ContractPair>,
  number
]): Map<string, Big> {
  const positions = positionsCache.toArray<ContractPositionRaw>();
  const settleCurrencies = Array.from(new Set(positions.map((o) => o.currency)));
  return settleCurrencies.reduce((p, c) => {
    const positions2 = positions.filter(({currency, size, isCross}) => currency === c && isCross && size > 0);
    p.set(
      c,
      positions2.length > 0 ? calcPostCost0(positions2, marketsCache, contractsCache, defaultTakerFeeEr) : Big(0)
    );
    return p;
  }, new Map());
}

function calcPostCost0(
  positions: ContractPositionRaw[],
  marketsCache: Map<string, ContractMarket>,
  contractsCache: Map<string, ContractPair>,
  defaultTakerFeeEr: number
) {
  const symbols = Array.from(new Set(positions.map(({symbol}) => symbol)));
  return symbols
    .map((symbol) => {
      const curPositions = positions.filter((item) => item.symbol === symbol);
      const isOneWay = curPositions.every(({posMode}) => posMode === 'OneWay');
      const market = marketsCache.get(symbol);
      const markPrice = market ? market.markPriceEp : 0;
      const defaultTakerFeeEr2 = contractUtil.getDefaultTakerFeeRate(contractsCache.get(symbol), defaultTakerFeeEr);

      if (isOneWay) {
        const position = curPositions.find(({posMode, posSide}) => posMode === 'OneWay' && posSide === 'Merged');
        if (!position) {
          return Big(0);
        }
        const {side, size, buyValueToCostEr, sellValueToCostEr} = position;
        const markValue = size && markPrice ? Big(size).times(markPrice) : Big(0);
        const valueToCostEr = side === 'Buy' ? buyValueToCostEr : sellValueToCostEr;
        return markValue.times(Big(valueToCostEr).minus(defaultTakerFeeEr2));
      } else {
        const posLong = curPositions.find(({posMode, posSide}) => posMode === 'Hedged' && posSide === 'Long');
        const posShort = curPositions.find(({posMode, posSide}) => posMode === 'Hedged' && posSide === 'Short');
        const longSize = posLong?.size || 0;
        const shortSize = posShort?.size || 0;
        const morePosition = longSize > shortSize ? posLong : posShort;
        if (!morePosition) {
          return Big(0);
        }
        const netPartSize = Big(longSize).minus(shortSize).abs();
        const netPartValue = netPartSize.gt(0) ? Big(netPartSize).times(markPrice) : Big(0);
        const {side, buyValueToCostEr, sellValueToCostEr} = morePosition;
        const valueToCostEr = side === 'Buy' ? buyValueToCostEr : sellValueToCostEr;
        return netPartValue.times(Big(valueToCostEr).minus(defaultTakerFeeEr2));
      }
    })
    .reduce((p, c) => Big(p).plus(c), Big(0));
}
